import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouteMatch } from 'react-router-dom';
import * as T from 'prop-types';

import { OnboardingForm } from './form/OnboardingForm';
import { AuthContext } from '../commons/auth/AuthContext';
import { withCookieAwareAuth } from '../commons/auth/withCookieAwareAuth';
import ErrorPage from '../commons/ErrorPage';
import { LoginForm } from '../commons/auth/LoginForm';
import SessionExpired from '../commons/auth/SessionExpired';
import LoadingPage from '../commons/LoadingPage';
import {
    GenericMessagePage,
    ActionRequiredPage,
} from '../commons/GenericMessagePage';
import {
    onboardingDataRecordedMessageElement,
    getInvalidUrlMessageElement,
    consultationsWorldEmailLink,
} from '../commons/MessageComponents';
import {
    validateOnboardLink,
    authenticateOnboardLink,
    reportIssueWithOnboarding,
    pingCheck,
} from './onboardingRoutes';

const OnboardFormContainer = (props) => {
    const { auth } = useContext(AuthContext);
    const [submitSucceeded, setSubmitSucceeded] = useState();
    const [submitErrors, setSubmitErrors] = useState();

    if (!auth.isAuthenticated) {
        return (
            <div className="mt-24">
                <LoginForm authenticateLink={authenticateOnboardLink} />
            </div>
        );
    }

    // if there are submitErrors, and it is not because of 401, i.e. expired token
    if (submitErrors && submitErrors?.response?.status !== 401) {
        return <ErrorPage />;
    }

    // if submission was successful.
    if (submitSucceeded) {
        return (
            <div>
                <ActionRequiredPage
                    message={'Submission successful.'}
                    subMessage={onboardingDataRecordedMessageElement}
                />
            </div>
        );
    }

    // for all other cases, keep displaying the form
    return (
        <div>
            {auth.isExpired && (
                <SessionExpired authenticateLink={authenticateOnboardLink} />
            )}
            <OnboardingForm
                setSubmitSucceeded={setSubmitSucceeded}
                setSubmitErrors={setSubmitErrors}
                {...props}
            />
        </div>
    );
};

OnboardFormContainer.propTypes = {
    consultantCountry: T.string,
    initialPageNumber: T.number,
};

const handleOnDigitalSignatureComplete = (props) => {
    // After digital signature completion, navigate to page 3 (Terms Signed page)
    if (props.setInitialPageNumber) {
        props.setInitialPageNumber(3);
    }
};

/**
 * Cookie-aware authentication container for onboarding.
 */
const CookieAwareAuthContainer = withCookieAwareAuth({
    validateSession: pingCheck,
    onDigitalSignatureComplete: handleOnDigitalSignatureComplete,
    reportIssue: reportIssueWithOnboarding,
    emailLink: consultationsWorldEmailLink,
    ChildComponent: OnboardFormContainer,
});

const AuthAwareOnboardForm = (props) => {
    // when the user loads the form, it is unauthenticated.
    const [auth, setAuth] = useState({ isAuthenticated: false });
    const [initialPageNumber, setInitialPageNumber] = useState(1);

    return (
        <AuthContext.Provider value={{ auth, setAuth }}>
            <CookieAwareAuthContainer
                {...props}
                initialPageNumber={initialPageNumber}
                setInitialPageNumber={setInitialPageNumber}
            />
        </AuthContext.Provider>
    );
};

AuthAwareOnboardForm.propTypes = {
    consultantCountry: PropTypes.string,
};

export const OnboardAuthenticateFormContainer = (props) => {
    const [linkStatus, setLinkStatus] = useState();
    const routeMatch = useRouteMatch();
    const { url } = routeMatch.params;

    const setOnboardingUrlStatusDetails = (urlStatusDetails) => {
        setLinkStatus(urlStatusDetails.status);
        props.setConsultantCountry(urlStatusDetails.country);
    };

    // validate link when it changes.
    useEffect(() => {
        validateOnboardLink(url, setOnboardingUrlStatusDetails);
    }, [url]);

    switch (linkStatus) {
        case 'VALID':
            return (
                <AuthAwareOnboardForm
                    consultantCountry={props.consultantCountry}
                />
            );

        case 'INVALID':
            return (
                <ErrorPage
                    message={'Invalid URL'}
                    subMessage={getInvalidUrlMessageElement(
                        consultationsWorldEmailLink
                    )}
                />
            );

        case 'EXPIRED':
            return (
                <ErrorPage
                    message={'The link you entered has expired.'}
                    subMessage={getInvalidUrlMessageElement(
                        consultationsWorldEmailLink
                    )}
                />
            );

        case 'SUBMITTED':
            return (
                <GenericMessagePage
                    message={'Submission successful.'}
                    subMessage={onboardingDataRecordedMessageElement}
                />
            );

        default:
            return <LoadingPage />;
    }
};

OnboardAuthenticateFormContainer.propTypes = {
    consultantCountry: PropTypes.string,
    setConsultantCountry: PropTypes.func,
};
